import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
  useAnalytics,
  getDatabaseContainerType,
  getSelectionsExperimentAttributes,
} from '@databases/analytics';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import {
  type DraggingItem,
  DragType,
  useDragItems,
  type HorizontalPositions,
} from '@databases/drag';
import {
  VERT_DRAG_HANDLER_ICON_WIDTH,
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { isNativeEmbedEditorPlugin, isStandalone } from '@databases/modes';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectIsEntireEntrySelected,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS, RESIZABLE } from '@databases/table-resizer';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  COMPACT_NUMBERED_COLUMN_WIDTH,
  NATIVE_EMBED_DRAG_HANDLE_SPACE,
  STANDALONE_OVERFLOW_SHADOW_BOTTOM_TRIM_PX,
  STANDALONE_OVERFLOW_SHADOW_TOP_TRIM_PX,
  NUMBERED_COLUMN_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
  TABLE_CLASSNAMES,
} from '@databases/utils/constants';
import { getHiddenFields } from '@databases/utils/database/view/view';
import useContainerScrollPosition from '@databases/utils/hooks/useContainerScrollPosition';
import { afterNextLayout } from '@databases/utils/lib/raf';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import useView from '@databases/views/useView';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';
import { useSnapshot } from 'valtio';

import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { type RefMapWithSizes, useRefMapWithSizes } from '../../../lazy-render';
import { shouldShowEntryPrefix } from '../../../utils';

import AddFieldButton from './AddFieldButton';
import { type EntryIsSelectedMap, EntryPrefixShadow } from './EntryPrefix';
import HeaderCheckbox from './HeaderCheckbox';
import SelectionsHeaderCheckbox from './SelectionsHeaderCheckbox';
import TableLayoutHeaderCell, { Th } from './TableLayoutHeaderCell';
import { DRAG_ITEM, ADD_ITEM } from './TableLayoutRows';
import useHasNumberedEntries from './UseHasNumberedEntries';
import { useOverflowShadow } from './hooks/useOverflowShadow';

export interface TableLayoutHeaderRowProps {
  contentId: string;
  canEditContent: boolean;
  canEditFields: boolean;
  couldEditFields: boolean;
  containerLeft: number;
  containerHeight: number;
  tableBodyRef: React.MutableRefObject<HTMLTableSectionElement | null>;
  isConfluencePanelOpen: boolean;
  visibleTableContentHeight: number;
}

const TR = styled.tr<{ $selectionsPerformanceGap?: boolean }>`
  z-index: ${Z_INDEX.TABLE_LAYOUT_HEADER_ROW};
  padding-top: ${token('space.100')};
  height: ${token('space.500', '40px')};
  margin-bottom: ${(props) =>
    props.$selectionsPerformanceGap ? token('space.025', '2px') : '0'};
`;

const StyledEntryPrefixTh = styled.th<{
  hasNumberedEntries: boolean;
  dragHandlerTdElementWidth: number;
  isVisualRefreshM1Enabled: boolean;
}>`
  width: ${(props) =>
    props.hasNumberedEntries
      ? isNativeEmbedEditorPlugin()
        ? COMPACT_NUMBERED_COLUMN_WIDTH + NATIVE_EMBED_DRAG_HANDLE_SPACE
        : NUMBERED_COLUMN_WIDTH
      : 0}px;
  visibility: ${(props) => (props.hasNumberedEntries ? 'visible' : 'hidden')};
  left: ${(props) =>
    isNativeEmbedEditorPlugin() ? 0 : props.dragHandlerTdElementWidth / 2}px;
  position: sticky;
  ${() =>
    isNativeEmbedEditorPlugin() &&
    css`
      box-sizing: border-box;
      padding: ${token('space.025', '4px')};
    `}
  background: ${(props) =>
    props.isVisualRefreshM1Enabled
      ? `${token('elevation.surface.sunken')}`
      : `${token('elevation.surface', '#FFFFF')}`};
  z-index: ${Z_INDEX.TABLE_ROW};

  // Checkbox positioning
  > * {
    margin-top: ${() =>
      isNativeEmbedEditorPlugin() ? '0px' : token('space.negative.050', '-4px')};
  }

  svg {
    width: 24px !important;
    height: 24px !important;
  }
`;

const TableLayoutHeaderRow = React.memo(
  React.forwardRef<HTMLTableRowElement | null, TableLayoutHeaderRowProps>(
    function TableLayoutHeaderRow(
      {
        contentId,
        canEditContent,
        canEditFields,
        couldEditFields,
        containerHeight,
        containerLeft,
        tableBodyRef,
        isConfluencePanelOpen,
        visibleTableContentHeight,
      },
      ref
    ) {
      const { view } = useView();
      const { formatMessage } = useIntl();

      const containerScrollPosition = useContainerScrollPosition(
        'scrollable-inner-container'
      );

      const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
        'databases-frontend-visual-refresh-table-m1'
      );

      const isSelectionsPerformanceEnabled = getExperimentValue(
        'databasesSelectionsPerformance',
        'isEnabled',
        {
          fireExperimentExposure: false,
        }
      );

      const dragHandlerTdElementWidth = isVisualRefreshM1Enabled
        ? DRAG_HANDLER_TD_ELEMENT_WIDTH
        : VERT_DRAG_HANDLER_ICON_WIDTH;

      const { enableOverflowShadow, topOffset, getOverflowShadowGradientStyle } =
        useOverflowShadow({
          isConfluencePanelOpen,
        });

      const overflowShadowTopPx = isStandalone()
        ? topOffset - STANDALONE_OVERFLOW_SHADOW_TOP_TRIM_PX
        : topOffset;
      const overflowShadowHeightPx = isStandalone()
        ? visibleTableContentHeight +
          STANDALONE_OVERFLOW_SHADOW_TOP_TRIM_PX -
          STANDALONE_OVERFLOW_SHADOW_BOTTOM_TRIM_PX
        : visibleTableContentHeight;

      const selectionsReader = getService('SelectionsReader');

      const [
        fieldIds,
        isLocked,
        hiddenFields,
        selectedFieldIds,
        entryIsSelectedMap,
        hasNoEntries,
      ] = defaultStoreUtils.useEqualSelector((state) => {
        const databaseDraftView = selectDatabaseDraftByContentId(state, {
          contentId,
          viewSettings: view.settings,
          includeDeleted: true,
        });
        const fieldIds = databaseDraftView.fields;
        const entryIsSelectedMap: EntryIsSelectedMap = databaseDraftView.entries.reduce(
          (acc: EntryIsSelectedMap, entryId: string) => {
            acc[entryId] = selectIsEntireEntrySelected(state, {
              entryId,
            });
            return acc;
          },
          {} as EntryIsSelectedMap
        );
        const hasNoEntries = databaseDraftView.entries.length === 0;

        return [
          fieldIds,
          databaseDraftView.meta.isLocked,
          getHiddenFields(view.settings.fields),
          selectSelectedFieldIds(state, { contentId }),
          entryIsSelectedMap,
          hasNoEntries,
        ] as const;
      });

      const prevFieldIdsRef = useRef(fieldIds);
      const [fieldToScroll, setFieldToScroll] = useState<string | null>(null);
      const {
        currentFieldOperation,
        fieldIdSelectedForProcessing,
        currentEntryOperation,
      } = useSnapshot(aiFirstCreationStreamingState);
      const { operationType, isStreaming } = useDatabaseStreamState();
      const isEditStreaming = isStreaming && operationType === 'edit';

      useEffect(() => {
        // check if new field has been added
        if (fieldIds.length > prevFieldIdsRef.current.length) {
          const newFieldIds = fieldIds.filter(
            (id) => !prevFieldIdsRef.current.includes(id)
          );
          if (newFieldIds.length > 0) {
            if (!isStreaming) {
              setFieldToScroll(newFieldIds[0]);
            }
          }
        }
        prevFieldIdsRef.current = fieldIds;
      }, [fieldIds, isStreaming]);

      useEffect(() => {
        // Don't scroll horizontally when currentEntryOperation is undefined (i.e adding new entry)
        if (
          isEditStreaming &&
          fieldIdSelectedForProcessing &&
          currentEntryOperation === 'edit'
        ) {
          setFieldToScroll(fieldIdSelectedForProcessing);
        }
      }, [
        isEditStreaming,
        fieldIdSelectedForProcessing,
        operationType,
        currentEntryOperation,
      ]);

      const showEntryPrefix = shouldShowEntryPrefix(view);
      const hasNumberedEntries = useHasNumberedEntries({ contentId }) && showEntryPrefix;
      const columnIds = useMemo(() => {
        const result = [...fieldIds];
        result.push(ADD_ITEM);

        return result;
      }, [fieldIds]);
      const columnRefMap: RefMapWithSizes<any> = useRefMapWithSizes({
        getPositions: ({ sizes }) => {
          const columnPositions: Record<string, HorizontalPositions> = {};
          let currentLeft = 0;
          for (const fieldId of columnIds) {
            const left = currentLeft;
            const width =
              fieldId === DRAG_ITEM
                ? 0
                : sizes[fieldId]?.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH;
            columnPositions[fieldId] = { left, width };
            currentLeft += width;
          }

          return columnPositions;
        },
      });

      useEffect(() => {
        if (!fieldToScroll) {
          return;
        }

        const container = document.getElementById('scrollable-inner-container');
        const position = columnRefMap.positions[fieldToScroll];

        if (!container || !position) {
          return;
        }

        requestAnimationFrame(() => {
          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width;
          const currentScrollLeft = container.scrollLeft;

          const columnLeft = position.left;
          const columnWidth = position.width;
          const columnCenter = columnLeft + columnWidth / 2;

          const targetScrollLeft = columnCenter - containerWidth / 2;

          const maxScroll = container.scrollWidth - containerWidth;
          const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScroll));

          if (Math.abs(clampedScrollLeft - currentScrollLeft) > 1) {
            container.scrollTo({ left: clampedScrollLeft, behavior: 'smooth' });
          }

          setFieldToScroll(null);
        });
      }, [fieldToScroll, columnRefMap.positions, setFieldToScroll]);

      function extractIndex(data: Record<string, unknown>) {
        const { fieldIndex } = data;
        if (typeof fieldIndex !== 'number') {
          return null;
        }
        return fieldIndex;
      }

      const [draggingItem, setDraggingItem] = useState<DraggingItem>();

      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const selectionsActions = getService('SelectionsActions');
      const client = useAnalytics();

      const deleteField =
        fieldIdSelectedForProcessing &&
        isEditStreaming &&
        currentFieldOperation === 'delete';

      /**
       * Select field during AI first creation deletion.
       * Selection logic moved from Redux thunk `selectFieldSingle`.
       * When AI is deleting a field, we want to select it to show visual feedback.
       */
      useEffect(() => {
        if (deleteField) {
          requestAnimationFrame(() => {
            selectionsActions.selectFields([fieldIdSelectedForProcessing]);
            setFieldToScroll(fieldIdSelectedForProcessing);
          });
        }
      }, [
        fieldIdSelectedForProcessing,
        selectionsActions,
        isEditStreaming,
        setFieldToScroll,
        deleteField,
      ]);

      const onDrop = useCallback(
        (draggingItem: DraggingItem) => {
          if (!draggingItem) {
            return;
          }

          const { idx, ids } = draggingItem;
          if (!ids) {
            return;
          }
          batch(() => {
            collabDatabaseBatch(store, contentId, () => {
              // Reverse the fieldIds if dragging to the left to ensure
              // relative order is maintained for the selected fields
              const lowestSelectedFieldId = fieldIds.indexOf(ids[0]);
              const selectedFieldIds =
                lowestSelectedFieldId < idx ? ids : ids.slice().reverse();
              for (const fieldId of selectedFieldIds) {
                void dispatch(
                  addChangeToDraft(contentId, {
                    type: DatabaseChangeType.REORDER_FIELD,
                    fieldId: fieldId,
                    newIndex: idx,
                    hiddenFields,
                  })
                );
              }
            });
          });
          void client.sendColumnReorderedEvent({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
            ...getSelectionsExperimentAttributes(contentId),
          });
        },
        [client, contentId, dispatch, hiddenFields, store, fieldIds]
      );

      const {
        handleDragLeave,
        handleDragEnter,
        handleDragEnd,
        handleDrag,
        handleDragOver,
      } = useDragItems({
        dragDirection: 'horizontal',
        ids: fieldIds,
        rowOrColIds: columnIds,
        contentId,
        positions: columnRefMap.positions,
        dragType: DragType.FIELD_COLUMNS,
        onDrop,
        onSetDraggingItem: setDraggingItem,
        topOrLeftPadding: 50 - containerLeft,
      });

      useEffect(() => {
        return combine(
          monitorForElements({
            canMonitor({ source }) {
              return source.data.type === DragType.FIELD_COLUMNS;
            },
            onDrop({ location, source }) {
              const destination = location.current.dropTargets[0];
              if (!destination) {
                return;
              }
              const startIndex = extractIndex(source.data);
              const indexOfTarget = extractIndex(destination.data);
              if (startIndex === null || indexOfTarget === null) {
                return;
              }
              const closestEdgeOfTarget = extractClosestEdge(destination.data);

              const currentSelectedFieldIds = selectionsReader.getFieldIdsFromSelection();

              onDrop({
                // Special handling for last field
                idx:
                  indexOfTarget +
                  (indexOfTarget + 1 === fieldIds.length &&
                  closestEdgeOfTarget === 'right'
                    ? 1
                    : 0),
                ids: currentSelectedFieldIds,
              });

              // Re-select fields at their new positions so highlights re-render
              if (isSelectionsPerformanceEnabled && currentSelectedFieldIds.length > 0) {
                afterNextLayout(() =>
                  selectionsActions.selectFields(currentSelectedFieldIds)
                );
              }
            },
          })
        );
      }, [
        onDrop,
        fieldIds,
        selectedFieldIds,
        isSelectionsPerformanceEnabled,
        selectionsReader,
        selectionsActions,
      ]);

      const hideAddNewFieldButton = columnIds.length === 1 && columnIds[0] === 'add';

      const renderAddFieldsButton = () => {
        if (isVisualRefreshM1Enabled && hideAddNewFieldButton) {
          return null;
        }

        if (isLocked && couldEditFields) {
          return (
            <Tooltip content={() => 'Structure is locked'} position="top">
              <AddFieldButton contentId={contentId} currentViewId={view.id} isDisabled />
            </Tooltip>
          );
        } else if (canEditFields) {
          return <AddFieldButton contentId={contentId} currentViewId={view.id} />;
        }

        return null;
      };

      const showHeaderCheckbox =
        isVisualRefreshM1Enabled && isPluginEnabled(PluginKey.CELL_SELECTION);

      const headerCheckbox = showHeaderCheckbox ? (
        isSelectionsPerformanceEnabled ? (
          <SelectionsHeaderCheckbox />
        ) : (
          <HeaderCheckbox entryIsSelectedMap={entryIsSelectedMap} contentId={contentId} />
        )
      ) : null;

      const isNativeEmbedWithCheckbox = isNativeEmbedEditorPlugin() && showHeaderCheckbox;

      return (
        <TR
          $selectionsPerformanceGap={isSelectionsPerformanceEnabled}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          id="table-layout-header-row"
          data-testid="table-layout-header-row"
          ref={ref}
        >
          {canEditContent && showEntryPrefix && !isNativeEmbedEditorPlugin() && (
            <th
              className={IGNORED_TABLE_ELEMENT_CLASS}
              style={{
                position: 'sticky',
                width: `${dragHandlerTdElementWidth / 2}px`,
                background: token('elevation.surface', '#FFFFF'),
                left: 0,
                zIndex: Z_INDEX.ROW_SELECT_BUTTON,
              }}
              aria-hidden="true"
            >
              {/* For visual refresh we are adding a border radius to table header row. Due to the way table header row is being rendered, we are able to apply border radius but
      we run into an overflow issue on scroll where the the underlying table cell borders remain visible and extend beyond the rounded corners during horizontal scrolling
      Usually an overflow: hidden will solve this problem, but because we have an extra th element at the beginning and end of the row with a position of sticky this causes unwanted issues
      To resolve, we use clip-path to hide the extended table rows for now and we can revisit if we refactor our table layout
      */}
              {isVisualRefreshM1Enabled && (
                <>
                  <div
                    style={{
                      width: token('space.100'),
                      height: token('space.100'),
                      backgroundColor: token('elevation.surface', '#FFFFFF'),
                      position: 'absolute',
                      top: '0',
                      right: token('space.negative.075', '-6px'),
                      clipPath: `path('M 0 8 L 0 0 L 8 0 C 8 0, 0 1, 0 12 Z')`,
                    }}
                  />
                </>
              )}
            </th>
          )}
          <StyledEntryPrefixTh
            className="entry-number-table-header"
            hasNumberedEntries={hasNumberedEntries}
            dragHandlerTdElementWidth={dragHandlerTdElementWidth}
            isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
            aria-label={
              isVisualRefreshM1Enabled && isPluginEnabled(PluginKey.CELL_SELECTION)
                ? formatMessage(messages['table-header.select-all-entries'])
                : undefined
            }
          >
            {isVisualRefreshM1Enabled && isNativeEmbedEditorPlugin() && (
              <>
                <div
                  style={{
                    width: token('space.150'),
                    height: token('space.150'),
                    backgroundColor: token('elevation.surface', '#FFFFFF'),
                    position: 'absolute',
                    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                    top: '-1px',
                    left: token('space.negative.025', '-2px'),
                    clipPath: `path('M 0 12 L 0 0 L 12 0 C 12 0, 0 1, 0 12 Z')`,
                  }}
                />
              </>
            )}
            {isNativeEmbedWithCheckbox ? (
              <Box
                xcss={xcss({
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  overflow: 'hidden',
                })}
              >
                <div
                  style={{
                    width: NATIVE_EMBED_DRAG_HANDLE_SPACE,
                    flexShrink: 0,
                  }}
                />
                {headerCheckbox}
              </Box>
            ) : (
              <>
                {headerCheckbox}
                {!isVisualRefreshM1Enabled && (
                  <EntryPrefixShadow
                    style={{
                      display: containerScrollPosition > 0 ? 'block' : 'none',
                    }}
                  />
                )}
              </>
            )}
          </StyledEntryPrefixTh>
          {columnIds.map((fieldId) =>
            fieldId !== DRAG_ITEM && fieldId !== ADD_ITEM ? (
              <TableLayoutHeaderCell
                key={fieldId}
                canEditFields={canEditFields}
                couldEditFields={couldEditFields}
                contentId={contentId}
                fieldId={fieldId}
                currentViewId={view.id}
                className={RESIZABLE}
                ref={{
                  current: {
                    tableBodyRef: tableBodyRef,
                    headerFieldRef: columnRefMap.refs[fieldId],
                  },
                }}
                isFirstInRow={fieldIds[0] === fieldId}
                isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                isDraggingActive={!!draggingItem}
                containerHeight={containerHeight}
                isDragOver={false}
                fieldIndex={columnIds.indexOf(fieldId)}
                fieldToScroll={fieldToScroll}
                setFieldToScroll={setFieldToScroll}
              />
            ) : (
              fieldId !== DRAG_ITEM &&
              fieldId === ADD_ITEM && (
                <Th
                  key={fieldId}
                  className={classNames([TABLE_CLASSNAMES.ADD_FIELD], {
                    [IGNORED_TABLE_ELEMENT_CLASS]: true,
                  })}
                  containerHeight={containerHeight}
                  isDragOver={false}
                >
                  {isVisualRefreshM1Enabled && enableOverflowShadow && !hasNoEntries && (
                    <>
                      {isNativeEmbedEditorPlugin() && (
                        <Box
                          testId="table-layout-header-row-overflow-shadow-border"
                          xcss={xcss({
                            position: 'absolute',
                            width: '0px',
                            top: `${topOffset}px`,
                            left: 'space.0',
                            pointerEvents: 'none',
                            height: `${visibleTableContentHeight}px`,
                            borderLeftWidth: 'border.width',
                            borderLeftStyle: 'solid',
                            borderLeftColor: 'color.border',
                          })}
                          style={{
                            zIndex: Z_INDEX.SCROLL_FADE_GRADIENT,
                          }}
                        />
                      )}
                      {isStandalone() && (
                        <Box
                          testId="table-layout-header-row-overflow-shadow-right-border"
                          xcss={xcss({
                            position: 'absolute',
                            width: '0px',
                            top: `${overflowShadowTopPx}px`,
                            right: '8px',
                            pointerEvents: 'none',
                            height: `${overflowShadowHeightPx}px`,
                            borderLeftWidth: 'border.width',
                            borderLeftStyle: 'solid',
                            borderLeftColor: 'color.border',
                          })}
                          style={{
                            zIndex: Z_INDEX.SCROLL_FADE_GRADIENT,
                          }}
                        />
                      )}
                      <Box
                        testId="table-layout-header-row-overflow-shadow"
                        xcss={xcss({
                          position: 'absolute',
                          width: '10px',
                          top: `${overflowShadowTopPx}px`,
                          pointerEvents: 'none',
                          right: `${isNativeEmbedEditorPlugin() ? 0 : 8}px`,
                          height: `${overflowShadowHeightPx}px`,
                        })}
                        style={{
                          ...(getOverflowShadowGradientStyle() as any),
                          zIndex: Z_INDEX.SCROLL_FADE_GRADIENT,
                        }}
                      />
                    </>
                  )}
                  {renderAddFieldsButton()}
                </Th>
              )
            )
          )}
        </TR>
      );
    }
  )
);

export default TableLayoutHeaderRow;
